/**
 * SchemaEditor — Editor visual de schemas OpenAPI
 *
 * Permite ao admin criar e editar schemas (modelos de dados) de forma visual,
 * sem precisar de editar YAML/JSON manualmente.
 *
 * Os schemas são guardados na spec OpenAPI do produto.
 */

import React, { useState, useCallback } from 'react';
import yaml from 'js-yaml';
import { ClipboardList, X, ArrowUp, ArrowDown } from 'lucide-react';

// ── Tipos ─────────────────────────────────────────────────────────────────

type PropertyType = 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';
type StringFormat = 'none' | 'date' | 'date-time' | 'email' | 'uuid' | 'uri' | 'password';

interface SchemaProperty {
  id: string;
  name: string;
  type: PropertyType;
  format?: StringFormat;
  description?: string;
  required: boolean;
  example?: string;
  enum?: string[];
  items?: { type: PropertyType; $ref?: string };
  $ref?: string; // reference to another schema
}

interface Schema {
  id: string;
  name: string;
  description?: string;
  properties: SchemaProperty[];
}

interface SchemaEditorProps {
  currentSpec: string;
  onSave: (updatedSpec: string) => void;
  isSaving?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function generateId() {
  return Math.random().toString(36).slice(2, 9);
}

function parseSchemas(spec: string): Schema[] {
  if (!spec) return [];
  try {
    const trimmed = spec.trim();
    const parsed =
      trimmed.startsWith('{') || trimmed.startsWith('[')
        ? JSON.parse(trimmed)
        : (yaml.load(trimmed) as any);
    const components = parsed?.components?.schemas || {};
    return Object.entries(components).map(([name, schema]: [string, any]) => ({
      id: generateId(),
      name,
      description: schema.description || '',
      properties: Object.entries(schema.properties || {}).map(
        ([propName, prop]: [string, any]) => ({
          id: generateId(),
          name: propName,
          type: (prop.type || 'string') as PropertyType,
          format: (prop.format || 'none') as StringFormat,
          description: prop.description || '',
          required: (schema.required || []).includes(propName),
          example: prop.example !== undefined ? String(prop.example) : '',
          enum: prop.enum || [],
          $ref: prop.$ref,
          items: prop.items,
        })
      ),
    }));
  } catch {
    return [];
  }
}

function schemasToOpenAPI(schemas: Schema[], existingSpec: string): string {
  try {
    const trimmed = existingSpec ? existingSpec.trim() : '{}';
    const isJson = trimmed.startsWith('{') || trimmed.startsWith('[');

    const parsed: any = existingSpec ? (isJson ? JSON.parse(trimmed) : yaml.load(trimmed)) : {};

    if (!parsed.components) parsed.components = {};
    parsed.components.schemas = {};

    schemas.forEach((schema) => {
      const required = schema.properties.filter((p) => p.required).map((p) => p.name);
      const properties: Record<string, any> = {};

      schema.properties.forEach((prop) => {
        if (prop.$ref) {
          properties[prop.name] = { $ref: prop.$ref };
        } else {
          const p: any = { type: prop.type };
          if (prop.format && prop.format !== 'none') p.format = prop.format;
          if (prop.description) p.description = prop.description;
          if (prop.example) {
            p.example =
              prop.type === 'number' || prop.type === 'integer'
                ? Number(prop.example)
                : prop.example;
          }
          if (prop.enum && prop.enum.length > 0) p.enum = prop.enum;
          if (prop.type === 'array' && prop.items) p.items = prop.items;
          properties[prop.name] = p;
        }
      });

      parsed.components.schemas[schema.name] = {
        type: 'object',
        ...(schema.description ? { description: schema.description } : {}),
        ...(required.length > 0 ? { required } : {}),
        properties,
      };
    });

    return isJson ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed);
  } catch {
    return existingSpec;
  }
}

// ── Componente principal ──────────────────────────────────────────────────

export const SchemaEditor: React.FC<SchemaEditorProps> = ({ currentSpec, onSave, isSaving }) => {
  const [schemas, setSchemas] = useState<Schema[]>(() => parseSchemas(currentSpec));
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | null>(
    () => parseSchemas(currentSpec)[0]?.id || null
  );
  const [showAddSchema, setShowAddSchema] = useState(false);
  const [newSchemaName, setNewSchemaName] = useState('');
  const [newSchemaDesc, setNewSchemaDesc] = useState('');

  const selectedSchema = schemas.find((s) => s.id === selectedSchemaId) || null;

  const handleSave = useCallback(() => {
    const updated = schemasToOpenAPI(schemas, currentSpec);
    onSave(updated);
  }, [schemas, currentSpec, onSave]);

  // ── Schema CRUD ──────────────────────────────────────────────────────────

  const addSchema = () => {
    if (!newSchemaName.trim()) return;
    const schema: Schema = {
      id: generateId(),
      name: newSchemaName.trim().replace(/\s+/g, ''),
      description: newSchemaDesc.trim(),
      properties: [],
    };
    setSchemas((prev) => [...prev, schema]);
    setSelectedSchemaId(schema.id);
    setShowAddSchema(false);
    setNewSchemaName('');
    setNewSchemaDesc('');
  };

  const deleteSchema = (id: string) => {
    setSchemas((prev) => prev.filter((s) => s.id !== id));
    if (selectedSchemaId === id) {
      setSelectedSchemaId(schemas.find((s) => s.id !== id)?.id || null);
    }
  };

  const updateSchema = (id: string, updates: Partial<Schema>) => {
    setSchemas((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  // ── Property CRUD ────────────────────────────────────────────────────────

  const addProperty = (schemaId: string) => {
    const prop: SchemaProperty = {
      id: generateId(),
      name: 'novoCampo',
      type: 'string',
      format: 'none',
      description: '',
      required: false,
      example: '',
      enum: [],
    };
    setSchemas((prev) =>
      prev.map((s) => (s.id === schemaId ? { ...s, properties: [...s.properties, prop] } : s))
    );
  };

  const updateProperty = (schemaId: string, propId: string, updates: Partial<SchemaProperty>) => {
    setSchemas((prev) =>
      prev.map((s) =>
        s.id === schemaId
          ? {
              ...s,
              properties: s.properties.map((p) => (p.id === propId ? { ...p, ...updates } : p)),
            }
          : s
      )
    );
  };

  const deleteProperty = (schemaId: string, propId: string) => {
    setSchemas((prev) =>
      prev.map((s) =>
        s.id === schemaId ? { ...s, properties: s.properties.filter((p) => p.id !== propId) } : s
      )
    );
  };

  const moveProperty = (schemaId: string, propId: string, direction: 'up' | 'down') => {
    setSchemas((prev) =>
      prev.map((s) => {
        if (s.id !== schemaId) return s;
        const idx = s.properties.findIndex((p) => p.id === propId);
        if (idx < 0) return s;
        const newIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= s.properties.length) return s;
        const props = [...s.properties];
        [props[idx], props[newIdx]] = [props[newIdx], props[idx]];
        return { ...s, properties: props };
      })
    );
  };

  const TYPE_OPTIONS: PropertyType[] = [
    'string',
    'number',
    'integer',
    'boolean',
    'array',
    'object',
  ];
  const FORMAT_OPTIONS: StringFormat[] = [
    'none',
    'date',
    'date-time',
    'email',
    'uuid',
    'uri',
    'password',
  ];

  const TYPE_COLORS: Record<PropertyType, string> = {
    string: '#2563eb',
    number: '#7c3aed',
    integer: '#7c3aed',
    boolean: '#d97706',
    array: '#059669',
    object: '#dc2626',
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Schema Editor</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Define os modelos de dados (schemas) da API de forma visual
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAddSchema(true)} className="btn-secondary text-sm">
            + Novo Schema
          </button>
          <button onClick={handleSave} disabled={isSaving} className="btn-primary text-sm">
            {isSaving ? 'A guardar...' : 'Guardar Schemas'}
          </button>
        </div>
      </div>

      {schemas.length === 0 ? (
        <div className="card text-center py-12">
          <ClipboardList className="w-12 h-12 text-primary-500 mx-auto mb-3" />
          <p className="text-slate-600 dark:text-slate-300 font-medium">Nenhum schema definido</p>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            Crie schemas para documentar os modelos de dados da API
          </p>
          <button onClick={() => setShowAddSchema(true)} className="btn-primary mt-4 text-sm">
            + Criar Primeiro Schema
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {/* Lista de schemas */}
          <div className="col-span-3">
            <div className="card p-0 overflow-hidden">
              <div className="bg-slate-50 dark:bg-slate-800/60 px-3 py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide">
                  Schemas ({schemas.length})
                </span>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {schemas.map((schema) => (
                  <div
                    key={schema.id}
                    onClick={() => setSelectedSchemaId(schema.id)}
                    className={`px-3 py-2.5 cursor-pointer flex items-center justify-between group hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${
                      selectedSchemaId === schema.id
                        ? 'bg-primary-50 dark:bg-primary-950/30 border-l-2 border-primary-500'
                        : ''
                    }`}
                  >
                    <div>
                      <p
                        className={`text-sm font-medium ${selectedSchemaId === schema.id ? 'text-primary-700 dark:text-primary-400' : 'text-slate-800 dark:text-slate-200'}`}
                      >
                        {schema.name}
                      </p>
                      <p className="text-xs text-slate-400">{schema.properties.length} campos</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSchema(schema.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-rose-400 hover:text-rose-600 text-xs transition-opacity"
                      title="Eliminar schema"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Editor do schema seleccionado */}
          <div className="col-span-9">
            {selectedSchema ? (
              <div className="card space-y-4">
                {/* Schema header */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label text-xs">Nome do Schema</label>
                    <input
                      type="text"
                      value={selectedSchema.name}
                      onChange={(e) =>
                        updateSchema(selectedSchema.id, {
                          name: e.target.value.replace(/\s+/g, ''),
                        })
                      }
                      className="input text-sm font-mono"
                      placeholder="NomeDoSchema"
                    />
                  </div>
                  <div>
                    <label className="label text-xs">Descrição</label>
                    <input
                      type="text"
                      value={selectedSchema.description || ''}
                      onChange={(e) =>
                        updateSchema(selectedSchema.id, { description: e.target.value })
                      }
                      className="input text-sm"
                      placeholder="Descrição do schema..."
                    />
                  </div>
                </div>

                {/* Propriedades */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">
                      Propriedades ({selectedSchema.properties.length})
                    </span>
                    <button
                      onClick={() => addProperty(selectedSchema.id)}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      + Adicionar Campo
                    </button>
                  </div>

                  {selectedSchema.properties.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                      <p className="text-sm text-gray-500">Nenhum campo definido</p>
                      <button
                        onClick={() => addProperty(selectedSchema.id)}
                        className="text-xs text-primary-600 hover:underline mt-1"
                      >
                        + Adicionar primeiro campo
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Header da tabela */}
                      <div className="grid grid-cols-12 gap-2 px-2 py-1 bg-gray-50 rounded text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <div className="col-span-3">Nome</div>
                        <div className="col-span-2">Tipo</div>
                        <div className="col-span-2">Formato</div>
                        <div className="col-span-3">Descrição</div>
                        <div className="col-span-1 text-center">Req.</div>
                        <div className="col-span-1 text-center">Acções</div>
                      </div>

                      {selectedSchema.properties.map((prop, idx) => (
                        <div
                          key={prop.id}
                          className="grid grid-cols-12 gap-2 items-center p-2 rounded-lg border border-gray-100 hover:border-gray-200 bg-white"
                        >
                          {/* Nome */}
                          <div className="col-span-3">
                            <input
                              type="text"
                              value={prop.name}
                              onChange={(e) =>
                                updateProperty(selectedSchema.id, prop.id, { name: e.target.value })
                              }
                              className="input text-xs font-mono py-1"
                              placeholder="nomeCampo"
                            />
                          </div>

                          {/* Tipo */}
                          <div className="col-span-2">
                            <select
                              value={prop.type}
                              onChange={(e) =>
                                updateProperty(selectedSchema.id, prop.id, {
                                  type: e.target.value as PropertyType,
                                })
                              }
                              className="select text-xs py-1"
                              style={{ color: TYPE_COLORS[prop.type] }}
                            >
                              {TYPE_OPTIONS.map((t) => (
                                <option key={t} value={t} style={{ color: TYPE_COLORS[t] }}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Formato */}
                          <div className="col-span-2">
                            {prop.type === 'string' ? (
                              <select
                                value={prop.format || 'none'}
                                onChange={(e) =>
                                  updateProperty(selectedSchema.id, prop.id, {
                                    format: e.target.value as StringFormat,
                                  })
                                }
                                className="select text-xs py-1"
                              >
                                {FORMAT_OPTIONS.map((f) => (
                                  <option key={f} value={f}>
                                    {f === 'none' ? '—' : f}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-xs text-gray-400 px-2">—</span>
                            )}
                          </div>

                          {/* Descrição */}
                          <div className="col-span-3">
                            <input
                              type="text"
                              value={prop.description || ''}
                              onChange={(e) =>
                                updateProperty(selectedSchema.id, prop.id, {
                                  description: e.target.value,
                                })
                              }
                              className="input text-xs py-1"
                              placeholder="Descrição..."
                            />
                          </div>

                          {/* Required */}
                          <div className="col-span-1 flex justify-center">
                            <input
                              type="checkbox"
                              checked={prop.required}
                              onChange={(e) =>
                                updateProperty(selectedSchema.id, prop.id, {
                                  required: e.target.checked,
                                })
                              }
                              className="w-4 h-4 text-primary-600 rounded"
                              title="Campo obrigatório"
                            />
                          </div>

                          {/* Acções */}
                          <div className="col-span-1 flex items-center justify-center gap-1">
                            <button
                              onClick={() => moveProperty(selectedSchema.id, prop.id, 'up')}
                              disabled={idx === 0}
                              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-20 text-xs"
                              title="Mover para cima"
                            >
                              <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => moveProperty(selectedSchema.id, prop.id, 'down')}
                              disabled={idx === selectedSchema.properties.length - 1}
                              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-20 text-xs"
                              title="Mover para baixo"
                            >
                              <ArrowDown className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => deleteProperty(selectedSchema.id, prop.id)}
                              className="text-rose-400 hover:text-rose-600 text-xs"
                              title="Eliminar campo"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Preview JSON */}
                <details className="mt-2">
                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                    Ver JSON gerado
                  </summary>
                  <pre className="mt-2 text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-48 text-gray-700">
                    {JSON.stringify(
                      {
                        type: 'object',
                        ...(selectedSchema.description
                          ? { description: selectedSchema.description }
                          : {}),
                        required: selectedSchema.properties
                          .filter((p) => p.required)
                          .map((p) => p.name),
                        properties: Object.fromEntries(
                          selectedSchema.properties.map((p) => [
                            p.name,
                            {
                              type: p.type,
                              ...(p.format && p.format !== 'none' ? { format: p.format } : {}),
                              ...(p.description ? { description: p.description } : {}),
                            },
                          ])
                        ),
                      },
                      null,
                      2
                    )}
                  </pre>
                </details>
              </div>
            ) : (
              <div className="card text-center py-12 text-gray-500">
                Seleccione um schema para editar
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Novo Schema */}
      {showAddSchema && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Novo Schema</h3>
            <div className="space-y-3">
              <div>
                <label className="label">Nome do Schema *</label>
                <input
                  type="text"
                  value={newSchemaName}
                  onChange={(e) => setNewSchemaName(e.target.value.replace(/\s+/g, ''))}
                  className="input"
                  placeholder="Ex: ConsultarResponse, CriarRequest"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">Use PascalCase, sem espaços</p>
              </div>
              <div>
                <label className="label">Descrição (opcional)</label>
                <input
                  type="text"
                  value={newSchemaDesc}
                  onChange={(e) => setNewSchemaDesc(e.target.value)}
                  className="input"
                  placeholder="Descrição do modelo de dados..."
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setShowAddSchema(false);
                  setNewSchemaName('');
                  setNewSchemaDesc('');
                }}
                className="btn-secondary flex-1"
              >
                Cancelar
              </button>
              <button
                onClick={addSchema}
                disabled={!newSchemaName.trim()}
                className="btn-primary flex-1"
              >
                Criar Schema
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
